import { useEffect, useState } from "react";
import { settings, tasks, orders } from "../lib/api";
import type { SystemInfo, Task, OrderStats } from "../lib/api";
import {
  Package,
  Users,
  ListTodo,
  Globe,
  TrendingUp,
  Clock,
  DollarSign,
  Play,
  Square,
  RefreshCw,
} from "lucide-react";
import clsx from "clsx";

export default function Dashboard() {
  const [system, setSystem] = useState<SystemInfo | null>(null);
  const [activeTasks, setActiveTasks] = useState<Task[]>([]);
  const [orderStats, setOrderStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [sysInfo, taskList, stats] = await Promise.all([
        settings.system(),
        tasks.list(),
        orders.stats("day"),
      ]);
      setSystem(sysInfo);
      setActiveTasks(taskList.filter((t) => t.enabled));
      setOrderStats(stats);
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const handleStartAll = async () => {
    try {
      await tasks.startAll();
      fetchData();
    } catch (error) {
      console.error("Failed to start tasks", error);
    }
  };

  const handleStopAll = async () => {
    try {
      await tasks.stopAll();
      fetchData();
    } catch (error) {
      console.error("Failed to stop tasks", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  const stats = [
    { label: "Products", value: system?.products || 0, icon: Package, color: "text-blue-400" },
    { label: "Profiles", value: system?.profiles || 0, icon: Users, color: "text-purple-400" },
    { label: "Active Tasks", value: system?.tasks.active || 0, icon: ListTodo, color: "text-primary-500" },
    { label: "Proxies", value: system?.proxies || 0, icon: Globe, color: "text-green-400" },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex gap-4">
        <button onClick={handleStartAll} className="btn-primary flex items-center gap-2">
          <Play size={18} />
          Start All Tasks
        </button>
        <button onClick={handleStopAll} className="btn-secondary flex items-center gap-2">
          <Square size={18} />
          Stop All
        </button>
        <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <stat.icon className={clsx("w-10 h-10", stat.color)} />
            </div>
          </div>
        ))}
      </div>

      {/* Order Stats */}
      {orderStats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-success/20 rounded-lg">
                <TrendingUp className="text-success" size={24} />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Success Rate (24h)</p>
                <p className="text-2xl font-bold">{orderStats.successRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary-500/20 rounded-lg">
                <DollarSign className="text-primary-500" size={24} />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Spent (24h)</p>
                <p className="text-2xl font-bold">${orderStats.totalSpent.toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-info/20 rounded-lg">
                <Clock className="text-info" size={24} />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Avg Checkout Time</p>
                <p className="text-2xl font-bold">{(orderStats.avgCheckoutTime / 1000).toFixed(2)}s</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Tasks */}
      <div className="card">
        <h3 className="font-display text-lg font-semibold mb-4">Active Tasks</h3>
        {activeTasks.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No active tasks. Create one to get started!</p>
        ) : (
          <div className="space-y-3">
            {activeTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-4 bg-dark-700/50 rounded-lg border border-dark-600"
              >
                <div className="flex items-center gap-4">
                  {task.product.imageUrl ? (
                    <img
                      src={task.product.imageUrl}
                      alt={task.product.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-dark-600 rounded flex items-center justify-center">
                      <Package size={20} className="text-gray-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium">{task.product.name}</h4>
                    <p className="text-sm text-gray-400">
                      {task.product.retailer} | Qty: {task.quantity} | Profile: {task.profile.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Queue Status */}
                  {task.status === "in_queue" && task.queuePosition && (
                    <div className="text-right">
                      <p className="text-sm text-warning">In Queue: #{task.queuePosition}</p>
                      {task.queueEstimatedWait && (
                        <p className="text-xs text-gray-500">~{task.queueEstimatedWait}m wait</p>
                      )}
                    </div>
                  )}
                  {/* Status Badge */}
                  <span
                    className={clsx(
                      "status-badge",
                      task.status === "monitoring" && "bg-blue-500/20 text-blue-400",
                      task.status === "in_queue" && "bg-warning/20 text-warning",
                      task.status === "success" && "bg-success/20 text-success",
                      task.status === "failed" && "bg-error/20 text-error",
                      task.status === "idle" && "bg-gray-500/20 text-gray-400"
                    )}
                  >
                    {task.status}
                  </span>
                  {/* Success/Fail Count */}
                  <div className="text-right text-sm">
                    <span className="text-success">{task.successCount}</span>
                    <span className="text-gray-500">/</span>
                    <span className="text-error">{task.failCount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

